import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Grid, Button, Chip,
  CircularProgress, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Tooltip, LinearProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel,
  Select, MenuItem, Autocomplete, FormControlLabel, Switch,
} from '@mui/material';
import {
  Hotel, BedroomChild, CheckCircle, Build, CleaningServices,
  Add, OpenInNew, Refresh, HotelOutlined, LocalHospital,
  WarningAmber, PriorityHigh,
} from '@mui/icons-material';
import { ipdService, wardService, userService, patientService, insurerService, tpaService } from '../../services/api';
import TopBar from '../../components/TopBar';

const WARD_TYPE_ICONS = {
  ICU: '🏥', General: '🛏️', Private: '🚪', SemiPrivate: '🏨',
  NICU: '👶', Emergency: '🚨', Maternity: '🤱', Pediatric: '🧒',
};

const WARD_TYPE_COLORS = {
  ICU:        '#b71c1c', General:   '#1565c0', Private:  '#4a148c',
  SemiPrivate:'#1b5e20', NICU:      '#880e4f', Emergency:'#e65100',
  Maternity:  '#880e4f', Pediatric: '#01579b',
};

const ADMISSION_TYPES = ['Emergency','Elective','Transfer','Referral'];
const BILLING_TYPES = ['SelfPay','Cashless','Reimbursement'];

const STATUS_CHIP = {
  Active:      { bg: '#e3f2fd', color: '#1565c0' },
  Discharged:  { bg: '#e8f5e9', color: '#2e7d32' },
  LAMA:        { bg: '#fff3e0', color: '#e65100' },
  Transferred: { bg: '#f3e5f5', color: '#6a1b9a' },
  Expired:     { bg: '#eeeeee', color: '#616161' },
};

const toLocalDT = (d) => {
  const dt = new Date(d);
  const pad = n => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const calcAge = (dob) => {
  if (!dob) return null;
  return Math.floor((new Date() - new Date(dob)) / (1000 * 60 * 60 * 24 * 365.25));
};

const IpdDashboard = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canEdit = user.role === 'Admin' || user.role === 'Doctor' || user.role === 'Receptionist';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dischargeFilter, setDischargeFilter] = useState('all'); // all | today | overdue

  // Admission dialog state
  const [admitDialog, setAdmitDialog] = useState(false);
  const [admitForm, setAdmitForm] = useState({
    patientId: null, wardId: '', bedId: '', admittingDoctorId: null,
    admissionType: 'Elective', admissionDate: toLocalDT(new Date()),
    expectedDischargeDate: '', admissionDiagnosis: '', icd10Code: '',
    isMlc: false, mlcDetails: '', referredBy: '', referralFrom: '',
    billingType: 'SelfPay', insurerId: null, tpaId: null, policyNumber: '', memberId: '',
  });
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [wardList, setWardList] = useState([]);
  const [availableBeds, setAvailableBeds] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [tpas, setTpas] = useState([]);
  const [admitError, setAdmitError] = useState('');
  const [admitting, setAdmitting] = useState(false);

  // Discharge dialog state
  const [dischargeDialog, setDischargeDialog] = useState({ open: false, admission: null });
  const [dischargeForm, setDischargeForm] = useState({ status: 'Discharged', dischargeType: 'Improved', dischargeNotes: '', actualDischargeDate: toLocalDT(new Date()) });
  const [discharging, setDischarging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ipdService.dashboard();
      setData(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load doctors + wards for admission form
  useEffect(() => {
    if (!admitDialog) return;
    Promise.all([userService.getDoctors(), wardService.getAll(), insurerService.getAll(), tpaService.getAll()]).then(([dr, wr, ir, tr]) => {
      setDoctors(dr.data);
      setWardList(wr.data.filter(w => w.isActive));
      setInsurers(ir.data.filter(i => i.isActive));
      setTpas(tr.data.filter(t => t.isActive));
    });
  }, [admitDialog]);

  // Patient search debounce
  useEffect(() => {
    if (!patientSearch || patientSearch.length < 2) { setPatientOptions([]); return; }
    const t = setTimeout(async () => {
      try { const r = await patientService.searchPatients(patientSearch); setPatientOptions(r.data); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [patientSearch]);

  // Load available beds when ward changes
  useEffect(() => {
    if (!admitForm.wardId) { setAvailableBeds([]); return; }
    wardService.getBeds(admitForm.wardId).then(r => {
      setAvailableBeds((r.data.beds || []).filter(b => b.status === 'Available' || b.status === 'Reserved'));
    }).catch(() => setAvailableBeds([]));
  }, [admitForm.wardId]);

  const handleAdmit = async (e) => {
    e.preventDefault();
    if (!admitForm.patientId) { setAdmitError('Please select a patient.'); return; }
    if (!admitForm.wardId)    { setAdmitError('Please select a ward.'); return; }
    if (!admitForm.bedId)     { setAdmitError('Please select a bed.'); return; }
    setAdmitting(true);
    try {
      await ipdService.create({
        patientId: admitForm.patientId.id, bedId: parseInt(admitForm.bedId),
        wardId: parseInt(admitForm.wardId), admittingDoctorId: admitForm.admittingDoctorId?.id || null,
        admissionType: admitForm.admissionType,
        admissionDate: admitForm.admissionDate || toLocalDT(new Date()),
        expectedDischargeDate: admitForm.expectedDischargeDate || null,
        admissionDiagnosis: admitForm.admissionDiagnosis, icd10Code: admitForm.icd10Code,
        isMlc: admitForm.isMlc, mlcDetails: admitForm.mlcDetails,
        referredBy: admitForm.referredBy, referralFrom: admitForm.referralFrom,
        billingType: admitForm.billingType,
        insurerId: admitForm.insurerId?.id || null,
        tpaId: admitForm.tpaId?.id || null,
        policyNumber: admitForm.policyNumber, memberId: admitForm.memberId,
      });
      setAdmitDialog(false);
      load();
    } catch (err) {
      setAdmitError(err.response?.data?.message || 'Failed to admit patient');
    } finally { setAdmitting(false); }
  };

  const openDischarge = (admission) => {
    setDischargeDialog({ open: true, admission });
    setDischargeForm({ status: 'Discharged', dischargeType: 'Improved', dischargeNotes: '', actualDischargeDate: toLocalDT(new Date()) });
  };

  const handleDischarge = async (e) => {
    e.preventDefault();
    setDischarging(true);
    try {
      await ipdService.discharge(dischargeDialog.admission.id, {
        ...dischargeForm,
        actualDischargeDate: dischargeForm.actualDischargeDate || toLocalDT(new Date()),
      });
      setDischargeDialog({ open: false, admission: null });
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to process discharge');
    } finally { setDischarging(false); }
  };

  const filteredDischarges = (data?.todayDischarges || []).filter(a => {
    if (dischargeFilter === 'today') return !a.isOverdue;
    if (dischargeFilter === 'overdue') return a.isOverdue;
    return true;
  });

  const summary = data?.summary || {};
  const occupancyPct = summary.totalBeds > 0 ? Math.round((summary.occupied / summary.totalBeds) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="IPD — Inpatient Department" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* ── Summary Cards ── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Beds', value: summary.totalBeds ?? '—', icon: <HotelOutlined />, color: '#1565c0', bg: '#e3f2fd' },
            { label: 'Occupied', value: summary.occupied ?? '—', sub: `${occupancyPct}%`, icon: <Hotel />, color: '#c62828', bg: '#ffebee' },
            { label: 'Available', value: summary.available ?? '—', icon: <CheckCircle />, color: '#2e7d32', bg: '#e8f5e9' },
            { label: 'Reserved', value: summary.reserved ?? '—', icon: <BedroomChild />, color: '#f57f17', bg: '#fff8e1' },
            { label: 'Maintenance', value: (summary.underMaintenance ?? 0) + (summary.housekeeping ?? 0), icon: <Build />, color: '#757575', bg: '#f5f5f5' },
          ].map(s => (
            <Grid item xs={6} sm={4} md={2.4} key={s.label}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: s.bg, borderLeft: `4px solid ${s.color}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700} sx={{ color: s.color, lineHeight: 1 }}>
                      {s.value}
                    </Typography>
                    {s.sub && <Typography variant="caption" sx={{ color: s.color }}>{s.sub} occupancy</Typography>}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{s.label}</Typography>
                  </Box>
                  <Box sx={{ color: s.color, opacity: 0.6 }}>{s.icon}</Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Toolbar ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>Ward Occupancy</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh"><IconButton size="small" onClick={load}><Refresh /></IconButton></Tooltip>
            {canEdit && (
              <Button variant="contained" startIcon={<Add />}
                onClick={() => { setAdmitError(''); setAdmitForm({ patientId: null, wardId: '', bedId: '', admittingDoctorId: null, admissionType: 'Elective', admissionDate: toLocalDT(new Date()), expectedDischargeDate: '', admissionDiagnosis: '', icd10Code: '', isMlc: false, mlcDetails: '', referredBy: '', referralFrom: '', billingType: 'SelfPay', insurerId: null, tpaId: null, policyNumber: '', memberId: '' }); setAdmitDialog(true); }}>
                New Admission
              </Button>
            )}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* ── Ward Grid ── */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {(data?.wards || []).map(ward => {
                const occ = ward.totalBeds > 0 ? (ward.occupied / ward.totalBeds) * 100 : 0;
                const barColor = occ >= 100 ? '#c62828' : occ >= 75 ? '#f57f17' : '#2e7d32';
                const typeColor = WARD_TYPE_COLORS[ward.wardType] || '#1565c0';
                return (
                  <Grid item xs={12} sm={6} lg={4} key={ward.id}>
                    <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, borderTop: `3px solid ${typeColor}`, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography fontSize="1.4rem">{WARD_TYPE_ICONS[ward.wardType] || '🛏️'}</Typography>
                            <Typography variant="subtitle1" fontWeight={700}>{ward.name}</Typography>
                          </Box>
                          {ward.floor && <Typography variant="caption" color="text.secondary">{ward.floor}</Typography>}
                        </Box>
                        <Chip size="small" label={ward.wardType} sx={{ bgcolor: typeColor + '20', color: typeColor, fontWeight: 600, fontSize: '0.7rem' }} />
                      </Box>

                      {/* Occupancy bar */}
                      <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Occupancy</Typography>
                          <Typography variant="caption" fontWeight={700} sx={{ color: barColor }}>
                            {ward.occupied}/{ward.totalBeds} ({Math.round(occ)}%)
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(occ, 100)}
                          sx={{ height: 8, borderRadius: 4,
                            '& .MuiLinearProgress-bar': { bgcolor: barColor },
                            bgcolor: barColor + '22' }}
                        />
                      </Box>

                      {/* Bed counts */}
                      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                        {[
                          { label: 'Available', val: ward.available, color: '#2e7d32' },
                          { label: 'Occupied',  val: ward.occupied,  color: '#c62828' },
                          { label: 'Reserved',  val: ward.reserved,  color: '#f57f17' },
                          { label: 'Maint.',    val: ward.underMaintenance + ward.housekeeping, color: '#757575' },
                        ].map(s => (
                          <Box key={s.label} sx={{ textAlign: 'center', minWidth: 48 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ color: s.color, lineHeight: 1.2 }}>{s.val}</Typography>
                            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                          </Box>
                        ))}
                      </Box>

                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<OpenInNew fontSize="small" />}
                        onClick={() => navigate(`/ipd/ward/${ward.id}`)}
                        sx={{ mt: 'auto', borderColor: typeColor, color: typeColor, '&:hover': { borderColor: typeColor, bgcolor: typeColor + '0a' } }}
                      >
                        View Beds
                      </Button>
                    </Paper>
                  </Grid>
                );
              })}
              {(data?.wards || []).length === 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <Typography color="text.secondary">No wards configured. Set them up in <strong>Ward Management</strong>.</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>

            {/* ── Today's Discharge Queue ── */}
            <Paper elevation={3} sx={{ borderRadius: 2 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LocalHospital color="action" />
                  <Typography variant="h6" fontWeight={700}>
                    Discharge Queue
                    {(data?.todayDischarges || []).length > 0 && (
                      <Chip size="small" label={(data?.todayDischarges || []).length} color="primary" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {['all','today','overdue'].map(f => (
                    <Chip key={f}
                      label={f === 'all' ? 'All' : f === 'today' ? 'Today' : '⚠ Overdue'}
                      size="small" clickable
                      color={dischargeFilter === f ? 'primary' : 'default'}
                      onClick={() => setDischargeFilter(f)}
                    />
                  ))}
                </Box>
              </Box>

              {filteredDischarges.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <CheckCircle sx={{ fontSize: 40, color: '#a5d6a7', mb: 1 }} />
                  <Typography color="text.secondary">No discharges scheduled for today.</Typography>
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                      <TableCell><strong>Bed</strong></TableCell>
                      <TableCell><strong>Patient</strong></TableCell>
                      <TableCell><strong>Ward</strong></TableCell>
                      <TableCell><strong>Doctor</strong></TableCell>
                      <TableCell><strong>Admitted</strong></TableCell>
                      <TableCell><strong>Expected</strong></TableCell>
                      <TableCell><strong>Days</strong></TableCell>
                      <TableCell><strong>Flags</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDischarges.map(a => (
                      <TableRow key={a.id} hover sx={{ bgcolor: a.isOverdue ? '#fff8f8' : 'inherit' }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{a.bed?.bedNumber || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {a.patient ? `${a.patient.firstName} ${a.patient.lastName}` : '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.patient?.gender}{a.patient?.dateOfBirth ? ` · ${calcAge(a.patient.dateOfBirth)}y` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>{a.ward?.name || '—'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {a.admittingDoctor ? `Dr. ${a.admittingDoctor.firstName} ${a.admittingDoctor.lastName}` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(a.admissionDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: a.isOverdue ? '#c62828' : 'inherit', fontWeight: a.isOverdue ? 700 : 400 }}>
                            {a.expectedDischargeDate ? new Date(a.expectedDischargeDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{a.daysInHospital}d</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {a.isOverdue && <Tooltip title="Overdue discharge"><WarningAmber sx={{ fontSize: 16, color: '#c62828' }} /></Tooltip>}
                            {a.isMlc && <Tooltip title="MLC Case"><PriorityHigh sx={{ fontSize: 16, color: '#c62828' }} /></Tooltip>}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {canEdit && (
                              <Button size="small" variant="contained" color="success"
                                sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
                                onClick={() => openDischarge(a)}>
                                Discharge
                              </Button>
                            )}
                            <Tooltip title="Open Patient Record">
                              <IconButton size="small" onClick={() => navigate(`/ipd/patient/${a.id}`)}>
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </>
        )}
      </Container>

      {/* ── New Admission Dialog ── */}
      <Dialog open={admitDialog} onClose={() => setAdmitDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>New IPD Admission</DialogTitle>
        <form onSubmit={handleAdmit}>
          <DialogContent>
            {admitError && <Typography color="error" sx={{ mb: 2 }}>{admitError}</Typography>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={patientOptions}
                  getOptionLabel={o => `${o.firstName} ${o.lastName} — ${o.phoneNumber}`}
                  value={admitForm.patientId}
                  onChange={(_, v) => setAdmitForm(p => ({ ...p, patientId: v }))}
                  onInputChange={(_, v) => setPatientSearch(v)}
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                  renderInput={params => <TextField {...params} label="Patient *" placeholder="Search by name or phone" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Admission Type</InputLabel>
                  <Select value={admitForm.admissionType} label="Admission Type *"
                    onChange={e => setAdmitForm(p => ({ ...p, admissionType: e.target.value }))}>
                    {ADMISSION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Ward</InputLabel>
                  <Select value={admitForm.wardId} label="Ward *"
                    onChange={e => setAdmitForm(p => ({ ...p, wardId: e.target.value, bedId: '' }))}>
                    {wardList.map(w => <MenuItem key={w.id} value={w.id}>{w.name} ({w.available} available)</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required disabled={!admitForm.wardId}>
                  <InputLabel>Bed</InputLabel>
                  <Select value={admitForm.bedId} label="Bed *"
                    onChange={e => setAdmitForm(p => ({ ...p, bedId: e.target.value }))}>
                    {availableBeds.length === 0
                      ? <MenuItem disabled value="">No available beds in this ward</MenuItem>
                      : availableBeds.map(b => <MenuItem key={b.id} value={b.id}>{b.bedNumber} ({b.bedType}) — {b.status}</MenuItem>)
                    }
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={doctors}
                  getOptionLabel={o => `Dr. ${o.firstName} ${o.lastName}${o.specialization ? ` — ${o.specialization}` : ''}`}
                  value={admitForm.admittingDoctorId}
                  onChange={(_, v) => setAdmitForm(p => ({ ...p, admittingDoctorId: v }))}
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                  renderInput={params => <TextField {...params} label="Admitting Doctor" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Admission Date & Time" type="datetime-local"
                  InputLabelProps={{ shrink: true }} value={admitForm.admissionDate}
                  onChange={e => setAdmitForm(p => ({ ...p, admissionDate: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Expected Discharge Date" type="date"
                  InputLabelProps={{ shrink: true }} value={admitForm.expectedDischargeDate}
                  onChange={e => setAdmitForm(p => ({ ...p, expectedDischargeDate: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="ICD-10 Code" value={admitForm.icd10Code}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                  onChange={e => setAdmitForm(p => ({ ...p, icd10Code: e.target.value.toUpperCase() }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Admission Diagnosis" value={admitForm.admissionDiagnosis}
                  placeholder="Primary diagnosis at time of admission"
                  onChange={e => setAdmitForm(p => ({ ...p, admissionDiagnosis: e.target.value }))} />
              </Grid>

              {/* ── Insurance Details ── */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1 }}>Insurance Details</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Billing Type</InputLabel>
                  <Select value={admitForm.billingType} label="Billing Type"
                    onChange={e => setAdmitForm(p => ({ ...p, billingType: e.target.value }))}>
                    {BILLING_TYPES.map(t => <MenuItem key={t} value={t}>{t === 'SelfPay' ? 'Self-Pay' : t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              {admitForm.billingType !== 'SelfPay' && (
                <>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={insurers}
                      getOptionLabel={o => o.name}
                      value={admitForm.insurerId}
                      onChange={(_, v) => setAdmitForm(p => ({ ...p, insurerId: v }))}
                      isOptionEqualToValue={(o, v) => o.id === v?.id}
                      renderInput={params => <TextField {...params} label="Insurer" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={tpas}
                      getOptionLabel={o => o.name}
                      value={admitForm.tpaId}
                      onChange={(_, v) => setAdmitForm(p => ({ ...p, tpaId: v }))}
                      isOptionEqualToValue={(o, v) => o.id === v?.id}
                      renderInput={params => <TextField {...params} label="TPA" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Policy Number" value={admitForm.policyNumber}
                      onChange={e => setAdmitForm(p => ({ ...p, policyNumber: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Member ID" value={admitForm.memberId}
                      onChange={e => setAdmitForm(p => ({ ...p, memberId: e.target.value }))} />
                  </Grid>
                </>
              )}

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Referred By" value={admitForm.referredBy}
                  onChange={e => setAdmitForm(p => ({ ...p, referredBy: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Referral From (Hospital/Clinic)" value={admitForm.referralFrom}
                  onChange={e => setAdmitForm(p => ({ ...p, referralFrom: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={<Switch checked={admitForm.isMlc} onChange={e => setAdmitForm(p => ({ ...p, isMlc: e.target.checked }))} color="error" />}
                    label={<Typography color={admitForm.isMlc ? 'error' : 'text.primary'} fontWeight={admitForm.isMlc ? 700 : 400}>MLC Case</Typography>}
                  />
                  {admitForm.isMlc && (
                    <TextField size="small" label="MLC / FIR Details" sx={{ flex: 1 }} value={admitForm.mlcDetails}
                      onChange={e => setAdmitForm(p => ({ ...p, mlcDetails: e.target.value }))} />
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdmitDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={admitting}>
              {admitting ? 'Admitting…' : 'Admit Patient'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Discharge Dialog ── */}
      <Dialog open={dischargeDialog.open} onClose={() => setDischargeDialog({ open: false, admission: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Process Discharge —{' '}
          {dischargeDialog.admission?.patient
            ? `${dischargeDialog.admission.patient.firstName} ${dischargeDialog.admission.patient.lastName}`
            : ''}
          {dischargeDialog.admission?.bed && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({dischargeDialog.admission.bed.bedNumber})
            </Typography>
          )}
        </DialogTitle>
        <form onSubmit={handleDischarge}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Discharge Status</InputLabel>
              <Select value={dischargeForm.status} label="Discharge Status *"
                onChange={e => setDischargeForm(p => ({ ...p, status: e.target.value }))}>
                {['Discharged','LAMA','Transferred','Expired'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Condition at Discharge</InputLabel>
              <Select value={dischargeForm.dischargeType} label="Condition at Discharge"
                onChange={e => setDischargeForm(p => ({ ...p, dischargeType: e.target.value }))}>
                {['Cured','Improved','Same','Deteriorated','LAMA','Transferred','Expired'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Actual Discharge Date & Time" type="datetime-local"
              InputLabelProps={{ shrink: true }} value={dischargeForm.actualDischargeDate}
              onChange={e => setDischargeForm(p => ({ ...p, actualDischargeDate: e.target.value }))} />
            <TextField fullWidth label="Discharge Notes" multiline rows={3} value={dischargeForm.dischargeNotes}
              placeholder="Brief summary, follow-up instructions..."
              onChange={e => setDischargeForm(p => ({ ...p, dischargeNotes: e.target.value }))} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDischargeDialog({ open: false, admission: null })}>Cancel</Button>
            <Button type="submit" variant="contained" color="success" disabled={discharging}>
              {discharging ? 'Processing…' : 'Confirm Discharge'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default IpdDashboard;
